import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import CustomButton from "../../components/Helpers/CustomButton";

export default function ReportBiometrics() {
    const { properties } = useSelector((state) => state.properties);
    const [biometricPropertyId, setBiometricPropertyId] = useState(0);
    const [biometricInactivityDays, setBiometricInactivityDays] = useState(3);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [propertyName, setPropertyName] = useState("");
    const [showReport, setShowReport] = useState(false);
    const [totalFound, setTotalFound] = useState(0);

    // Sorting state
    const [sortField, setSortField] = useState("days_since_activity");
    const [sortDirection, setSortDirection] = useState("desc");

    // New filter state variables
    const [skipNoHistory, setSkipNoHistory] = useState(false);
    const [onlyNoHistory, setOnlyNoHistory] = useState(false);
    const [filterMode, setFilterMode] = useState("inactivity"); // "inactivity", "no_history", "all"
    const [lastEntryDate, setLastEntryDate] = useState("");

    // Function to fetch the biometric inactivity data
    async function fetchBiometricData() {
        if (!biometricPropertyId) {
            alert("Please select a property");
            return;
        }

        setLoading(true);
        setShowReport(false);

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/biometric_inactivity_data`,
                data: {
                    property_id: biometricPropertyId,
                    days: biometricInactivityDays,
                    filter_mode: filterMode,
                    last_entry_date: lastEntryDate || null,
                },
            });

            // Set the property name for display
            const selectedProperty = properties.find((p) => p.value == biometricPropertyId);
            setPropertyName(selectedProperty ? selectedProperty.label : "Selected Property");

            // Apply client-side filtering if needed based on the skip/only flags
            let filteredData = response.data.users || [];

            // Apply local filters if API doesn't handle them
            if (skipNoHistory) {
                filteredData = filteredData.filter((user) => user.last_activity !== "Never");
            }

            if (onlyNoHistory) {
                filteredData = filteredData.filter((user) => user.last_activity === "Never");
            }

            // Set the report data
            setReportData(filteredData);
            setTotalFound(filteredData.length);
            setShowReport(true);
        } catch (error) {
            console.error("Error fetching biometric inactivity data:", error);
            alert("Error fetching biometric inactivity data");
        } finally {
            setLoading(false);
        }
    }

    // Handle filter mode change
    const handleFilterModeChange = (mode) => {
        setFilterMode(mode);

        // Reset incompatible options
        if (mode === "no_history") {
            setSkipNoHistory(false);
            setOnlyNoHistory(true);
        } else if (mode === "all") {
            setSkipNoHistory(false);
            setOnlyNoHistory(false);
            setBiometricInactivityDays(30); // Set a higher default for "all" mode
        }
    };

    // Sort data function
    const sortData = (field) => {
        // If clicking the same field, toggle direction
        if (field === sortField) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            // New field, set default direction
            setSortField(field);
            // For last_activity, default sort is newest first (desc)
            setSortDirection(field === "last_activity" ? "desc" : "asc");
        }
    };

    // Get sorted data
    const getSortedData = () => {
        if (!reportData || reportData.length === 0) return [];

        const sorted = [...reportData].sort((a, b) => {
            if (sortField === "last_activity") {
                const dateA = a.last_activity === "Never" ? new Date(0) : new Date(a.last_activity);
                const dateB = b.last_activity === "Never" ? new Date(0) : new Date(b.last_activity);
                return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
            } else if (sortField === "days_since_activity") {
                const valueA = a.days_since_activity === "N/A" ? Number.MAX_SAFE_INTEGER : parseInt(a.days_since_activity);
                const valueB = b.days_since_activity === "N/A" ? Number.MAX_SAFE_INTEGER : parseInt(b.days_since_activity);
                return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
            } else if (sortField === "user_name") {
                return sortDirection === "asc" ? a.user_name.localeCompare(b.user_name) : b.user_name.localeCompare(a.user_name);
            } else if (sortField === "booking_reference") {
                return sortDirection === "asc" ? a.booking_reference.localeCompare(b.booking_reference) : b.booking_reference.localeCompare(a.booking_reference);
            } else if (sortField === "property_name") {
                return sortDirection === "asc" ? a.property_name.localeCompare(b.property_name) : b.property_name.localeCompare(a.property_name);
            }
            return 0;
        });
        return sorted;
    };

    // Effect to update filter flags based on mode changes
    useEffect(() => {
        if (filterMode === "no_history") {
            setOnlyNoHistory(true);
            setSkipNoHistory(false);
        }
    }, [filterMode]);

    // Sort indicator component
    const SortIndicator = ({ field }) => {
        if (sortField !== field) return null;
        return <span className="ml-1">{sortDirection === "asc" ? "▲" : "▼"}</span>;
    };

    return (
        <>
            <div className="h-full w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
                <h1 className="text-2xl font-bold mb-6">Biometric Activity Report</h1>

                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Generate Biometric Report</h2>
                    <p className="mb-4 text-gray-600">This report analyzes biometric access data for residents. Use the filters below to customize your report.</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Property</label>
                            <select
                                className="mt-1 block w-full border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                onChange={(e) => setBiometricPropertyId(e.target.value)}
                                value={biometricPropertyId}
                            >
                                <option value="">Choose One</option>
                                <option value="all">All Properties</option>
                                {properties &&
                                    properties.length > 0 &&
                                    properties.map((p) => (
                                        <option key={p.value} value={p.value}>
                                            {p.label}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        {/* Report Type Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                            <div className="mt-1 grid grid-cols-3 gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleFilterModeChange("inactivity")}
                                    className={`py-2 px-3 text-sm font-medium rounded-lg ${filterMode === "inactivity" ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                                >
                                    Inactivity
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleFilterModeChange("no_history")}
                                    className={`py-2 px-3 text-sm font-medium rounded-lg ${filterMode === "no_history" ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                                >
                                    No History
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleFilterModeChange("all")}
                                    className={`py-2 px-3 text-sm font-medium rounded-lg ${filterMode === "all" ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                                >
                                    All Activity
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Conditional filters based on selected report type */}
                    {filterMode === "inactivity" && (
                        <div className="mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Inactivity Threshold (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="30"
                                        className="mt-1 block w-full border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                        value={biometricInactivityDays}
                                        onChange={(e) => setBiometricInactivityDays(parseInt(e.target.value, 10) || 3)}
                                    />
                                </div>
                                <div className="flex items-center space-x-6 mt-6">
                                    <div className="flex items-center">
                                        <input
                                            id="skip-no-history"
                                            type="checkbox"
                                            checked={skipNoHistory}
                                            onChange={(e) => {
                                                setSkipNoHistory(e.target.checked);
                                                if (e.target.checked) setOnlyNoHistory(false);
                                            }}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                        />
                                        <label htmlFor="skip-no-history" className="ml-2 block text-sm text-gray-700">
                                            Skip people with no history
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {filterMode === "all" && (
                        <div className="mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Show Activity Since Date</label>
                                    <input
                                        type="date"
                                        className="mt-1 block w-full border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                        value={lastEntryDate}
                                        onChange={(e) => setLastEntryDate(e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center space-x-6 mt-6">
                                    <div className="flex items-center">
                                        <input
                                            id="only-no-history"
                                            type="checkbox"
                                            checked={onlyNoHistory}
                                            onChange={(e) => {
                                                setOnlyNoHistory(e.target.checked);
                                                if (e.target.checked) setSkipNoHistory(false);
                                            }}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                        />
                                        <label htmlFor="only-no-history" className="ml-2 block text-sm text-gray-700">
                                            Only people with no history
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6">
                        <CustomButton
                            className="relative justify-center inline-flex text-white uppercase items-center px-8 py-2 rounded-full border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 shadow-md font-bold"
                            text="Generate Report"
                            onClick={() => fetchBiometricData()}
                            loading={loading}
                        />
                    </div>
                </div>

                {/* Report Results Section */}
                {showReport && (
                    <div className="bg-white rounded-lg shadow p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Report Results: {propertyName}</h2>
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">{totalFound} users found</span>
                        </div>

                        {reportData.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-green-600 text-lg">
                                    {filterMode === "inactivity"
                                        ? `Great news! All users have been active in the last ${biometricInactivityDays} days.`
                                        : filterMode === "no_history"
                                        ? "Great news! All users have biometric history."
                                        : "No data matching your search criteria."}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("user_name")}
                                            >
                                                User <SortIndicator field="user_name" />
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("booking_reference")}
                                            >
                                                Booking Reference <SortIndicator field="booking_reference" />
                                            </th>
                                            {/* Show property column only when viewing all properties */}
                                            {biometricPropertyId === "all" && (
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                    onClick={() => sortData("property_name")}
                                                >
                                                    Property <SortIndicator field="property_name" />
                                                </th>
                                            )}
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("last_activity")}
                                            >
                                                Last Activity <SortIndicator field="last_activity" />
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                onClick={() => sortData("days_since_activity")}
                                            >
                                                Inactive Days <SortIndicator field="days_since_activity" />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {getSortedData().map((user, index) => (
                                            <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{user.user_name}</div>
                                                            <div className="text-sm text-gray-500">{user.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <a
                                                        href={`/booking/url?user_id=${user.user_id}&booking_id=${user.booking_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {user.booking_reference}
                                                    </a>
                                                </td>
                                                {/* Show property name only when viewing all properties */}
                                                {biometricPropertyId === "all" && (
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">{user.property_name}</div>
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{user.last_activity !== "Never" ? new Date(user.last_activity).toLocaleString() : "Never"}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${
                                            user.days_since_activity === "N/A"
                                                ? "bg-red-100 text-red-800"
                                                : parseInt(user.days_since_activity) > 7
                                                ? "bg-red-100 text-red-800"
                                                : parseInt(user.days_since_activity) > 3
                                                ? "bg-yellow-100 text-yellow-800"
                                                : "bg-green-100 text-green-800"
                                        }`}
                                                    >
                                                        {user.days_since_activity}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
